import { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';

/**
 * 3D Secure Authentication Modal
 * Handles 3DS challenge in an iframe
 * Supports both 3DS1 (PAReq/PaRes) and 3DS2 (CReq/CRes)
 * Uses postMessage from backend to detect completion
 */
const ThreeDSecureModal = ({ visible, onHide, onComplete, paymentResult, orderId, transactionId }) => {
  const iframeRef = useRef(null);
  const formRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible && paymentResult) {
      // Check if we have 3DS data
      if (paymentResult.Status !== '3DAUTH') {
        setError('Invalid 3D Secure data');
        setIsLoading(false);
        return;
      }

      // Check for 3DS2 (CReq) or 3DS1 (PAReq)
      const has3DS2 = paymentResult.CReq && paymentResult.ACSURL;
      const has3DS1 = paymentResult.PAReq && paymentResult.ACSURL;

      if (!has3DS2 && !has3DS1) {
        setError('Missing 3D Secure authentication data');
        setIsLoading(false);
        return;
      }

      // Submit the form to trigger 3DS authentication
      setTimeout(() => {
        if (formRef.current) {
          formRef.current.submit();
          setIsLoading(false);
        }
      }, 100);
    }
  }, [visible, paymentResult]);

  // Listen for postMessage from 3DS iframe when authentication completes
  useEffect(() => {
    const handleMessage = (event) => {
      // The backend will send this message from the HTML response after processing 3DS
      if (event.data && event.data.threeDSComplete) {
        if (event.data.success) {
          // Backend already processed the result, just notify parent
          onComplete();
        } else {
          setError(event.data.error || '3D Secure authentication failed');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onComplete]);

  // Handle iframe load (detect when bank page loads)
  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const renderContent = () => {
    if (error) {
      return (
        <div className="p-4">
          <Message
            severity="error"
            text={error}
            className="w-full"
          />
        </div>
      );
    }

    if (!paymentResult || paymentResult.Status !== '3DAUTH') {
      return (
        <div className="p-4">
          <Message
            severity="warn"
            text="No 3D Secure authentication required"
            className="w-full"
          />
        </div>
      );
    }

    return (
      <div className="threeds-container">
        {isLoading && (
          <div className="flex justify-content-center align-items-center p-4">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
            <span className="ml-3">Loading authentication...</span>
          </div>
        )}

        {/* Hidden form to POST to ACS URL - supports both 3DS1 and 3DS2 */}
        <form
          ref={formRef}
          method="POST"
          action={paymentResult.ACSURL}
          target="threeds-iframe"
          style={{ display: 'none' }}
        >
          {/* 3DS2 fields */}
          {paymentResult.CReq && (
            <>
              <input type="hidden" name="creq" value={paymentResult.CReq} />
              <input type="hidden" name="threeDSSessionData" value={paymentResult.VPSTxId?.toString()?.replace(/[{}() \t\r\n]/g, '') || transactionId || orderId || ''} />
            </>
          )}
          
          {/* 3DS1 fields (fallback) */}
          {paymentResult.PAReq && (
            <>
              <input type="hidden" name="PaReq" value={paymentResult.PAReq} />
              <input type="hidden" name="MD" value={paymentResult.MD || orderId} />
              <input type="hidden" name="TermUrl" value={`https://www.sportendurance.co.uk/api/spa/order/payment/3dsecure?orderId=${orderId}`} />
            </>
          )}
        </form>

        {/* iframe to display 3DS challenge */}
        <iframe
          ref={iframeRef}
          name="threeds-iframe"
          title="3D Secure Authentication"
          onLoad={handleIframeLoad}
          style={{
            width: '100%',
            height: '500px',
            border: 'none',
            display: isLoading ? 'none' : 'block'
          }}
        />

        <div className="mt-3">
          <Message
            severity="info"
            text="Please complete the authentication with your card issuer. This window will close automatically when complete."
            className="w-full"
          />
        </div>
      </div>
    );
  };

  return (
    <Dialog
      header="Secure Payment Authentication"
      visible={visible}
      style={{ width: '600px' }}
      onHide={onHide}
      modal
      closable={!isLoading}
      closeOnEscape={!isLoading}
      className="threeds-modal"
    >
      {renderContent()}
    </Dialog>
  );
};

export default ThreeDSecureModal;
